import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowRight, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { DoodleHeart, DoodleStar, DoodleSquiggle } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = [
  { value: 1, emoji: '😢', label: 'Terrible', color: '#FF6B6B', bg: '#FFE0E0' },
  { value: 2, emoji: '😟', label: 'Bad', color: '#FFC8DD', bg: '#FFF0F5' },
  { value: 3, emoji: '😐', label: 'Okay', color: '#FDF0D5', bg: '#FFFCF0' },
  { value: 4, emoji: '😊', label: 'Good', color: '#B9FBC0', bg: '#F0FFF0' },
  { value: 5, emoji: '🤩', label: 'Amazing!', color: '#A2D2FF', bg: '#F0F7FF' },
];

export default function MoodTracker() {
  const [moods, setMoods] = useState([]);
  const [todayMood, setTodayMood] = useState(null);
  const [note, setNote] = useState('');
  const [selectedMood, setSelectedMood] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM

  const today = new Date().toISOString().split('T')[0];

  const fetchMoods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/moods?month=${month}`, { withCredentials: true });
      setMoods(data);
      const todayEntry = data.find(m => m.date === today);
      if (todayEntry) {
        setTodayMood(todayEntry.mood);
        setSelectedMood(todayEntry.mood);
        setNote(todayEntry.note || '');
      }
    } catch (e) {
      console.error('Failed to fetch moods:', e);
    }
  }, [month, today]);

  useEffect(() => { fetchMoods(); }, [fetchMoods]);

  const saveMood = async () => {
    if (!selectedMood) {
      toast.error('Pick a mood!');
      return;
    }
    try {
      await axios.post(`${API}/moods`, { mood: selectedMood, note, date: today }, { withCredentials: true });
      setTodayMood(selectedMood);
      toast.success('Mood logged!');
      fetchMoods();
    } catch (e) {
      toast.error('Failed to save mood');
    }
  };

  const changeMonth = (delta) => {
    const d = new Date(month + '-01');
    d.setMonth(d.getMonth() + delta);
    setMonth(d.toISOString().slice(0, 7));
  };

  // Build calendar grid for the month
  const buildCalendar = () => {
    const [year, mon] = month.split('-').map(Number);
    const firstDay = new Date(year, mon - 1, 1).getDay();
    const daysInMonth = new Date(year, mon, 0).getDate();
    
    const cells = [];
    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      cells.push({ day: null });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(mon).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const moodEntry = moods.find(m => m.date === dateStr);
      cells.push({ day: d, date: dateStr, mood: moodEntry?.mood, note: moodEntry?.note });
    }
    return cells;
  };

  const getMoodConfig = (val) => MOODS.find(m => m.value === val);

  const formatMonthLabel = () => {
    const [y, m] = month.split('-');
    return new Date(parseInt(y), parseInt(m) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const calendar = buildCalendar();

  return (
    <div className="space-y-4" data-testid="mood-tracker">
      {/* Today's Mood */}
      <div className="neo-card p-6" style={{
        backgroundImage: 'radial-gradient(circle, #D0D0D0 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }}>
        <h3 className="text-xl font-bold font-['Fredoka'] mb-1">How are you feeling today?</h3>
        <DoodleSquiggle width={100} color="#FFC8DD" className="mb-4" />

        <div className="flex justify-center gap-3 mb-4">
          {MOODS.map(m => (
            <button
              key={m.value}
              onClick={() => setSelectedMood(m.value)}
              className={`w-14 h-14 rounded-2xl border-2 border-black flex items-center justify-center text-2xl transition-all ${
                selectedMood === m.value 
                  ? 'shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] scale-110' 
                  : 'shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:scale-105'
              }`}
              style={{ backgroundColor: selectedMood === m.value ? m.color : m.bg }}
              data-testid={`mood-${m.value}`}
            >
              {m.emoji}
            </button>
          ))}
        </div>

        {selectedMood && (
          <p className="text-center font-bold font-['Fredoka'] text-lg mb-3" style={{ color: getMoodConfig(selectedMood)?.color }}>
            {getMoodConfig(selectedMood)?.label}
          </p>
        )}

        <div className="flex gap-2">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="neo-input flex-1"
            placeholder="Add a note about today... (optional)"
            data-testid="mood-note-input"
          />
          <button
            onClick={saveMood}
            className="neo-btn px-6 py-2 bg-[#B9FBC0]"
            data-testid="save-mood-btn"
          >
            {todayMood ? 'Update' : 'Save'}
          </button>
        </div>

        {todayMood && (
          <p className="text-center text-sm text-gray-400 mt-2">
            Mood logged for today <DoodleStar className="inline" size={14} color="#FFC8DD" />
          </p>
        )}
      </div>

      {/* Monthly Calendar */}
      <div className="neo-card p-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => changeMonth(-1)} className="neo-btn px-3 py-2 bg-white" data-testid="prev-month">
            <ArrowRight className="w-5 h-5 rotate-180" />
          </button>
          <h3 className="text-xl font-bold font-['Fredoka']">{formatMonthLabel()}</h3>
          <button onClick={() => changeMonth(1)} className="neo-btn px-3 py-2 bg-white" data-testid="next-month">
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center text-xs font-bold text-gray-400 uppercase">{d}</div>
          ))}
        </div>

        {/* Calendar cells */}
        <div className="grid grid-cols-7 gap-1">
          {calendar.map((cell, i) => {
            if (!cell.day) return <div key={i} />;
            const moodConfig = cell.mood ? getMoodConfig(cell.mood) : null;
            const isToday = cell.date === today;
            
            return (
              <div
                key={i}
                className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-sm relative ${
                  isToday ? 'border-2 border-black' : 'border-gray-200'
                }`}
                style={moodConfig ? { backgroundColor: moodConfig.bg } : {}}
                title={cell.note || ''}
              >
                <span className={`text-xs ${isToday ? 'font-bold' : 'text-gray-500'}`}>{cell.day}</span>
                {moodConfig && <span className="text-base">{moodConfig.emoji}</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
